"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Reveal } from "@/components/cosmic/Reveal";
import { Compass, Cpu, Brain, ShieldAlert, FlaskConical, BookOpen, ArrowRight, Sparkles } from "lucide-react";
interface KnowledgeAreaCard {
  title: string;
  number: string;
  desc: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const KNOWLEDGE_AREAS: KnowledgeAreaCard[] = [{
  title: "Philosophy as Investigation",
  number: "01",
  desc: "Examine existence, ethics, and epistemology as systematic inquiries rather than static doctrines.",
  icon: Compass
}, {
  title: "Logic Laboratory",
  number: "02",
  desc: "Test propositional validity with deterministic truth table generators and formal rule verifiers.",
  icon: Cpu
}, {
  title: "Critical Thinking Workshop",
  number: "03",
  desc: "Evaluate deductive, inductive, and abductive arguments through structured, hands-on rubrics.",
  icon: Brain
}, {
  title: "Fallacy Library",
  number: "04",
  desc: "Search and inspect formal and informal cognitive pitfalls with concrete, side-by-side examples.",
  icon: ShieldAlert
}, {
  title: "Knowledge Laboratory",
  number: "05",
  desc: "Investigate justified true belief, the Gettier problem, and criteria of empirical confirmation.",
  icon: FlaskConical
}, {
  title: "Glossary & References",
  number: "06",
  desc: "Cross-examine core philosophical concepts, historical roots, and academic literature citations.",
  icon: BookOpen
}];
function KnowledgeCardBody({
  item
}: {
  item: KnowledgeAreaCard;
}) {
  const Icon = item.icon;
  return <>
      {/* Top specular hairline accent */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent group-hover:via-primary/50 transition-colors" />

      <div>
        {/* Header: Icon & Area Index */}
        <div className="flex items-center justify-between mb-4">
          <div className="size-10 rounded-lg bg-card/80 border border-border/60 flex items-center justify-center backdrop-blur-xs text-primary group-hover:drop-shadow-[0_0_8px_var(--glow-primary)] transition-transform duration-300 group-hover:scale-110">
            <Icon className="size-5" />
          </div>
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/80">
            {item.number} // DISCIPLINE
          </span>
        </div>

        <h3 className="font-serif text-lg sm:text-xl font-bold tracking-tight text-foreground mb-2 group-hover:text-primary transition-colors leading-snug">
          {item.title}
        </h3>

        <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed">
          {item.desc}
        </p>
      </div>

      {/* Card footer CTA link */}
      <div className="mt-5 pt-3 border-t border-border/40 flex items-center justify-between text-primary font-serif text-xs font-semibold uppercase tracking-wider min-h-[32px]">
        <span>Open Module</span>
        <ArrowRight className="size-3.5 shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
      </div>
    </>;
}
export default function KnowledgeArchitecture() {
  return <section id="architecture-of-knowledge" data-nav="dark" className="relative py-16 sm:py-20 lg:py-28 w-full max-w-full overflow-x-clip text-foreground scroll-mt-12">
      <div className="relative z-10 max-w-[76rem] w-full mx-auto px-4 sm:px-6">
        {/* Hairline section divider */}
        <div className="ql-divider mb-14 sm:mb-16" aria-hidden="true" />

        <Reveal>
          <SectionHeading eyebrow="THE ARCHITECTURE OF KNOWLEDGE" title="Explore Reality. Examine Evidence. Understand How We Know." description="A free, connected learning world about how we know things: philosophy, logic, critical thinking, fallacies, and epistemology. Explore working tools including truth tables, argument testers, and a searchable fallacy library. Completely free, no accounts or paywalls, built on deterministic rule-based models and not AI." align="center" />
        </Reveal>

        {/* 6 compact cards in an analytical responsive grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mt-10 sm:mt-12">
          <Reveal delay={0} className="h-full">
            <Link to="/PhilosophyAsInvestigationPage" className="ql-card group flex flex-col justify-between h-full p-5 sm:p-6 block transition-all duration-300 hover:border-primary/60 hover:shadow-[0_0_24px_rgba(0,229,255,0.2)]">
              <KnowledgeCardBody item={KNOWLEDGE_AREAS[0]} />
            </Link>
          </Reveal>

          <Reveal delay={75} className="h-full">
            <Link to="/LogicLaboratoryPage" className="ql-card group flex flex-col justify-between h-full p-5 sm:p-6 block transition-all duration-300 hover:border-primary/60 hover:shadow-[0_0_24px_rgba(0,229,255,0.2)]">
              <KnowledgeCardBody item={KNOWLEDGE_AREAS[1]} />
            </Link>
          </Reveal>

          <Reveal delay={150} className="h-full">
            <Link to="/CriticalThinkingPage" className="ql-card group flex flex-col justify-between h-full p-5 sm:p-6 block transition-all duration-300 hover:border-primary/60 hover:shadow-[0_0_24px_rgba(0,229,255,0.2)]">
              <KnowledgeCardBody item={KNOWLEDGE_AREAS[2]} />
            </Link>
          </Reveal>

          <Reveal delay={225} className="h-full">
            <Link to="/FallacyLibraryPage" className="ql-card group flex flex-col justify-between h-full p-5 sm:p-6 block transition-all duration-300 hover:border-primary/60 hover:shadow-[0_0_24px_rgba(0,229,255,0.2)]">
              <KnowledgeCardBody item={KNOWLEDGE_AREAS[3]} />
            </Link>
          </Reveal>

          <Reveal delay={300} className="h-full">
            <Link to="/KnowledgeLaboratoryPage" className="ql-card group flex flex-col justify-between h-full p-5 sm:p-6 block transition-all duration-300 hover:border-primary/60 hover:shadow-[0_0_24px_rgba(0,229,255,0.2)]">
              <KnowledgeCardBody item={KNOWLEDGE_AREAS[4]} />
            </Link>
          </Reveal>

          <Reveal delay={375} className="h-full">
            <Link to="/GlossaryAndReferencesPage" className="ql-card group flex flex-col justify-between h-full p-5 sm:p-6 block transition-all duration-300 hover:border-primary/60 hover:shadow-[0_0_24px_rgba(0,229,255,0.2)]">
              <KnowledgeCardBody item={KNOWLEDGE_AREAS[5]} />
            </Link>
          </Reveal>
        </div>

        {/* Primary CTA centered below cards */}
        <Reveal delay={450} className="mt-10 sm:mt-12 flex justify-center">
          <Link to="/ArchitectureOfKnowledgePage" className="ql-btn min-h-[48px] px-8 py-3.5 text-sm font-serif uppercase tracking-wider justify-center shadow-[0_0_24px_rgba(0,229,255,0.35)]">
            <Sparkles className="size-4 shrink-0" />
            <span>ENTER THE KNOWLEDGE WORLD</span>
            <ArrowRight className="size-4 shrink-0" />
          </Link>
        </Reveal>
      </div>
    </section>;
}